import { cn } from "@faire/react/tools/cn";
import {
  isResponsiveProp,
  ResponsiveProp,
} from "@faire/react/viewport/ResponsiveProp";
import {
  getSpacing,
  getSpacingBase8,
  SpacerValueOrKey,
  SpacerValueOrKeyBase8,
} from "@faire/web/slate/spacing/getSpacing";
import React, { CSSProperties, memo } from "react";

import "./native-styles.css";

const applySpacerSizeVars = <T,>(
  definedSize: ResponsiveProp<T>,
  resolve: (value: T) => string,
  vars: Record<string, string | undefined>
): boolean => {
  if (isResponsiveProp(definedSize)) {
    vars["--f_spacer_size_mobile"] = resolve(definedSize.mobileAndAbove);
    vars["--f_spacer_size_tablet"] =
      definedSize.tabletAndAbove != null
        ? resolve(definedSize.tabletAndAbove)
        : vars["--f_spacer_size_mobile"];
    vars["--f_spacer_size_desktop"] =
      definedSize.desktopAndAbove != null
        ? resolve(definedSize.desktopAndAbove)
        : vars["--f_spacer_size_tablet"];
    vars["--f_spacer_size_xlarge"] =
      definedSize.xLargeAndAbove != null
        ? resolve(definedSize.xLargeAndAbove)
        : vars["--f_spacer_size_desktop"];
    vars["--f_spacer_size_xxlarge"] =
      definedSize.xxLargeDesktop != null
        ? resolve(definedSize.xxLargeDesktop)
        : vars["--f_spacer_size_xlarge"];
    return true;
  }
  vars["--f_spacer_size_mobile"] = resolve(definedSize);
  return false;
};

interface ISize {
  size: ResponsiveProp<SpacerValueOrKey>;
  /**
   * @deprecated prefer `size` (base 4) instead of `sizeBase8` (base 8) - matching Slate's new dimension tokens base
   */
  sizeBase8?: never;
  width?: never;
  /**
   * @deprecated prefer `width` (base 4) instead of `widthBase8` (base 8) - matching Slate's new dimension tokens base
   */
  widthBase8?: never;
  height?: never;
  /**
   * @deprecated prefer `height` (base 4) instead of `heightBase8` (base 8) - matching Slate's new dimension tokens base
   */
  heightBase8?: never;
}

interface ISizeBase8Deprecated {
  size?: never;
  /**
   * @deprecated prefer `size` (base 4) instead of `sizeBase8` (base 8) - matching Slate's new dimension tokens base
   */
  sizeBase8: ResponsiveProp<SpacerValueOrKeyBase8>;
  width?: never;
  /**
   * @deprecated prefer `width` (base 4) instead of `widthBase8` (base 8) - matching Slate's new dimension tokens base
   */
  widthBase8?: never;
  height?: never;
  /**
   * @deprecated prefer `height` (base 4) instead of `heightBase8` (base 8) - matching Slate's new dimension tokens base
   */
  heightBase8?: never;
}

interface IWidth {
  width: ResponsiveProp<SpacerValueOrKey>;
  /**
   * @deprecated prefer `width` (base 4) instead of `widthBase8` (base 8) - matching Slate's new dimension tokens base
   */
  widthBase8?: never;
  height?: never;
  /**
   * @deprecated prefer `height` (base 4) instead of `heightBase8` (base 8) - matching Slate's new dimension tokens base
   */
  heightBase8?: never;
  size?: never;
  /**
   * @deprecated prefer `size` (base 4) instead of `sizeBase8` (base 8) - matching Slate's new dimension tokens base
   */
  sizeBase8?: never;
}

interface IWidthBase8Deprecated {
  width?: never;
  /**
   * @deprecated prefer `width` (base 4) instead of `widthBase8` (base 8) - matching Slate's new dimension tokens base
   */
  widthBase8: ResponsiveProp<SpacerValueOrKeyBase8>;
  height?: never;
  /**
   * @deprecated prefer `height` (base 4) instead of `heightBase8` (base 8) - matching Slate's new dimension tokens base
   */
  heightBase8?: never;
  size?: never;
  /**
   * @deprecated prefer `size` (base 4) instead of `sizeBase8` (base 8) - matching Slate's new dimension tokens base
   */
  sizeBase8?: never;
}

interface IHeight {
  height: ResponsiveProp<SpacerValueOrKey>;
  /**
   * @deprecated prefer `height` (base 4) instead of `heightBase8` (base 8) - matching Slate's new dimension tokens base
   */
  heightBase8?: never;
  width?: never;
  /**
   * @deprecated prefer `width` (base 4) instead of `widthBase8` (base 8) - matching Slate's new dimension tokens base
   */
  widthBase8?: never;
  size?: never;
  /**
   * @deprecated prefer `size` (base 4) instead of `sizeBase8` (base 8) - matching Slate's new dimension tokens base
   */
  sizeBase8?: never;
}

interface IHeightBase8Deprecated {
  height?: never;
  /**
   * @deprecated prefer `height` (base 4) instead of `heightBase8` (base 8) - matching Slate's new dimension tokens base
   */
  heightBase8: ResponsiveProp<SpacerValueOrKeyBase8>;
  width?: never;
  /**
   * @deprecated prefer `width` (base 4) instead of `widthBase8` (base 8) - matching Slate's new dimension tokens base
   */
  widthBase8?: never;
  size?: never;
  /**
   * @deprecated prefer `size` (base 4) instead of `sizeBase8` (base 8) - matching Slate's new dimension tokens base
   */
  sizeBase8?: never;
}

type FixedSpacerProps =
  | ISize
  | ISizeBase8Deprecated
  | IWidth
  | IWidthBase8Deprecated
  | IHeight
  | IHeightBase8Deprecated;

export type ISpacerProps = Omit<
  React.AllHTMLAttributes<HTMLDivElement>,
  "as" | "size" | "height" | "width"
> &
  FixedSpacerProps;

const SpacerBase: React.FC<
  ISpacerProps & {
    ref?: React.Ref<HTMLDivElement>;
  }
// oxlint-disable-next-line complexity/complexity -- FIXME(CLI-2648): Complex function exceeds max cognitive complexity (20). Review top complexity contributors and denest/split accordingly.
> = ({
  ref,
  height: heightProp,
  heightBase8: heightBase8Prop,
  width: widthProp,
  widthBase8: widthBase8Prop,
  size: sizeProp,
  sizeBase8: sizeBase8Prop,
  className,
  style: styleProp,
  children,
  ...rest
}) => {
  let styles: CSSProperties = {};
  const vars: Record<string, string | undefined> = {};
  const classNames: string[] = [];

  const height = heightProp ?? heightBase8Prop;
  const width = widthProp ?? widthBase8Prop;
  const size = sizeProp ?? sizeBase8Prop;

  const base8Value = sizeBase8Prop ?? widthBase8Prop ?? heightBase8Prop;
  const base4Value = sizeProp ?? widthProp ?? heightProp;

  const definedSize = size ?? width ?? height;
  if (definedSize != null) {
    let isResponsive = false;
    if (base8Value != null) {
      isResponsive = applySpacerSizeVars(base8Value, getSpacingBase8, vars);
    } else if (base4Value != null) {
      isResponsive = applySpacerSizeVars(base4Value, getSpacing, vars);
    }
    const fbPrefix = isResponsive
      ? "f_spacer_variable"
      : "f_spacer_single_value";
    classNames.push(`${fbPrefix}_fb`);
    if ((size ?? width) != null) {
      classNames.push(`${fbPrefix}_min_width`);
    }
    if ((size ?? height) != null) {
      classNames.push(`${fbPrefix}_min_height`);
    }

    styles = {
      ...(width ? { height: 0 } : {}),
      ...(height ? { width: 0 } : {}),
    };
  }

  return (
    <div
      ref={ref}
      style={{ ...vars, ...styles, ...styleProp }}
      className={cn(className, "f_spacer_base", classNames)}
      {...rest}
    >
      {children}
    </div>
  );
};
/**
 * @trackcomponent
 * @link [Docs](https://slate.faire.team/component/spacer/web/spacer)
 */
export const Spacer = memo(SpacerBase);

export type IGrowingSpacerProps = Omit<
  React.AllHTMLAttributes<HTMLDivElement>,
  "as"
> &
  (
    | {
        base?: ResponsiveProp<SpacerValueOrKey>;
        /**
         * @deprecated prefer `base` (base 4) instead of `base8` (base 8) - matching Slate's new dimension tokens base
         */
        base8?: never;
      }
    | {
        base?: never;
        /**
         * @deprecated prefer `base` (base 4) instead of `base8` (base 8) - matching Slate's new dimension tokens base
         */
        base8?: ResponsiveProp<SpacerValueOrKeyBase8>;
      }
  );

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/component/spacer/web/growing-spacer)
 */
export const GrowingSpacer: React.FC<
  IGrowingSpacerProps & {
    ref?: React.Ref<HTMLDivElement>;
  }
> = ({
  ref,
  base: baseProp,
  base8: base8Prop,
  className,
  style: styleProp,
  children,
  ...rest
}) => {
  const vars: Record<string, string | undefined> = {};
  const classNames: string[] = [];

  const size = baseProp ?? base8Prop;

  if (size != null) {
    let isResponsive = false;
    if (base8Prop != null) {
      isResponsive = applySpacerSizeVars(base8Prop, getSpacingBase8, vars);
    } else if (baseProp != null) {
      isResponsive = applySpacerSizeVars(baseProp, getSpacing, vars);
    }
    classNames.push(
      isResponsive ? "f_spacer_variable_fb" : "f_spacer_single_value_fb"
    );
  }

  return (
    <div
      ref={ref}
      style={{ ...vars, ...styleProp }}
      className={cn(className, "f_spacer_grow", classNames)}
      {...rest}
    >
      {children}
    </div>
  );
};
